/**
 * A rule the assistant suggested for a company's playbook, under its tool row
 * in chat. It reads the company live, so it shows whether an admin has since
 * added or dismissed it; admins decide here or on the Playbook tab.
 */
import { ArrowUpRight, CheckCircle2, Plus, ScrollText } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useFirmPermissions } from "@/hooks/useFirmPermissions";
import { playbookActions } from "../actions";
import { appliesText, CATEGORY_LABEL } from "../playbookModel";
import { useCompany } from "../queries";
import { companyPath } from "../routes";
import type { PlaybookRule } from "../types";

export type PlaybookSuggestion = {
  company_id: string;
  company_name: string;
  rule: PlaybookRule;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The `playbook_suggestion` of a `propose_playbook_rule` result. */
export const playbookSuggestionOf = (
  output: unknown
): PlaybookSuggestion | null => {
  if (!isRecord(output) || !isRecord(output.playbook_suggestion)) return null;
  const s = output.playbook_suggestion;
  if (
    typeof s.company_id !== "string" ||
    typeof s.company_name !== "string" ||
    !isRecord(s.rule) ||
    typeof s.rule.id !== "string" ||
    typeof s.rule.text !== "string"
  ) {
    return null;
  }
  return s as unknown as PlaybookSuggestion;
};

type Props = { suggestion: PlaybookSuggestion; className?: string };

export const PlaybookSuggestionCard: FC<Props> = ({
  suggestion,
  className
}) => {
  const isAdmin = useFirmPermissions().can("edit_company_playbook");
  const company = useCompany(suggestion.company_id).data;
  const current = company?.playbook?.find((r) => r.id === suggestion.rule.id);
  const rule = current ?? suggestion.rule;
  const added = current?.status === "active";
  const gone = Boolean(company) && !current;
  const name = company?.name ?? suggestion.company_name;

  return (
    <div
      data-testid="playbook-suggestion-card"
      className={cn(
        "flex min-w-0 flex-col gap-1.5 rounded-md border border-line-2 bg-surface px-2.5 py-2 text-sm",
        className
      )}
    >
      <span className="flex items-center gap-1.5 text-sm text-fg-3">
        <ScrollText className="size-3" /> {name} playbook ·{" "}
        {CATEGORY_LABEL[rule.category] ?? rule.category}
        <Badge variant="secondary" className="ml-auto h-5 text-sm font-normal">
          {appliesText(rule)}
        </Badge>
      </span>
      <span
        className={cn("text-sm leading-4.5", gone && "text-fg-3 line-through")}
      >
        {rule.text}
      </span>
      {rule.source ? (
        <span className="text-sm text-fg-3">{rule.source}</span>
      ) : null}
      <span className="flex items-center gap-1.5">
        {added ? (
          <span className="flex items-center gap-1 text-sm text-success">
            <CheckCircle2 className="size-3.5" /> Added as Rule {rule.number}
          </span>
        ) : gone ? (
          <span className="text-sm text-fg-3">Dismissed</span>
        ) : isAdmin && company ? (
          <>
            <Button
              variant="primary"
              size="xs"
              onClick={() =>
                void playbookActions.decide(company, rule, "accept")
              }
            >
              <Plus /> Add to playbook
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                void playbookActions.decide(company, rule, "dismiss")
              }
            >
              Dismiss
            </Button>
          </>
        ) : (
          <span className="text-sm text-fg-3">
            Waiting for a firm admin to add or dismiss it.
          </span>
        )}
        <Button asChild size="xs" variant="ghost" className="ml-auto text-fg-3">
          <Link to={companyPath(suggestion.company_id, "playbook")}>
            Playbook <ArrowUpRight />
          </Link>
        </Button>
      </span>
    </div>
  );
};
